import React, { useRef, useState } from 'react';
import { jobListings } from '../mockData';
import { useNavigate } from 'react-router-dom';

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3 w-3"
  >
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

const JobCard = ({ job }) => {
  const navigate = useNavigate();
  const slug = (job.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <button
      className="group ml-[1px] flex h-[140px] w-full flex-col justify-between rounded-md border p-4 pt-3 ring-1 ring-transparent duration-200 hover:border-[#0A3900] hover:bg-gray-100/25 hover:ring-[#0A3900] text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent focus-visible:ring-2 focus-visible:ring-[#0A3900]"
      onClick={() => navigate(`/jobs/${job.id}/${slug}`)}
    >
      <div>
        <h3 className="line-clamp-1 text-base font-medium">{job.title}</h3>
        <p className="mt-1 text-sm text-gray-500">
          ${job.payRange.min}{job.payRange.min !== job.payRange.max ? `-$${job.payRange.max}` : ''}/hr
        </p>
      </div>
      <div className="flex w-full flex-row items-center justify-between gap-1">
        <div className="flex flex-row items-center gap-1 text-sm">
          {job.avatars && job.avatars.length > 0 && (
            <div className="ml-3 flex flex-row items-center gap-1">
              {job.avatars.slice(0, 3).map((avatar, i) => (
                <div
                  key={i}
                  className="-ml-3 h-6 w-6 overflow-hidden rounded-full border-[1.5px] border-white bg-gray-200 shadow-sm"
                >
                  <img
                    alt="Candidate profile"
                    loading="lazy"
                    width="24"
                    height="24"
                    className="h-full w-full object-cover"
                    src={avatar}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-gray-500">{job.hiredCount} hired recently</div>
        </div>
        <div className="flex flex-row items-center gap-1 text-gray-500 group-hover:text-[#0A3900]">
          <div className="text-sm">Apply</div>
          <div className="w-0 flex-shrink-0 overflow-hidden duration-200 group-hover:w-[10px]">
            <ArrowIcon />
          </div>
        </div>
      </div>
    </button>
  );
};

const JobListings = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
  };

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  // Use first 10 jobs for the grid (5 per row, 2 rows)
  const displayJobs = jobListings.slice(0, 10);

  return (
    <section className="mt-24 sm:block">
      {/* Header */}
      <div className="flex flex-row items-center justify-between">
        <h2 className="text-lg font-medium">Latest roles</h2>
        <div className="flex flex-row items-center gap-6">
          <div className="flex flex-row items-center gap-2">
            <button
              disabled={!canScrollLeft}
              className={`rounded-md border border-gray-100 p-1 transition-all hover:border-gray-200 ${
                !canScrollLeft ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              onClick={() => scroll(-1)}
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 512 512"
                className={`h-3.5 w-3.5 ${!canScrollLeft ? 'text-gray-200' : 'text-gray-500'}`}
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="48"
                  d="M328 112 184 256l144 144"
                />
              </svg>
            </button>
            <button
              disabled={!canScrollRight}
              className={`rounded-md border border-gray-100 p-1 transition-all hover:border-gray-200 ${
                !canScrollRight ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              onClick={() => scroll(1)}
            >
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 512 512"
                className={`h-3.5 w-3.5 ${!canScrollRight ? 'text-gray-200' : 'text-gray-500'}`}
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="48"
                  d="m184 112 144 144-144 144"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable 2-row grid */}
      <div
        ref={scrollRef}
        className="-ml-[1.5px] mt-4 snap-x snap-mandatory overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
        onScroll={checkScroll}
      >
        <div
          className="grid auto-cols-max grid-flow-col grid-rows-2 gap-5 p-[2px]"
          style={{ gridAutoColumns: 'min(300px, 80vw)' }}
        >
          {displayJobs.map((job) => (
            <div key={job.id} className="snap-start" style={{ width: 'min(300px, 80vw)' }}>
              <JobCard job={job} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JobListings;
